import { initContract } from "@ts-rest/core"
import { z } from "zod"
import type { getSession } from "./lib/auth.js"
import type { enableSkillForUser } from "./lib/skill-enablement.js"
import type {
  createCheckoutSession,
  getStripePortalLink,
} from "./lib/stripe.js"
import type { getMosques, importTimesForLondon } from "./lib/mosques.js"
import type { getTimesForIntegrationByDay } from "./lib/times.js"
import type {
  createIntegration,
  deleteIntegration,
  getIntegrations,
  updateIntegration,
} from "./lib/integrations.js"
import type { getIcsForIntegration } from "./lib/ics.js"
import type {
  sendNotificationsForMinute,
  storeNotificationsForHour,
} from "./lib/notifications.js"
import type { scheduleSendNotifications } from "./lib/schedule-send-notifications.js"
import type { searchLocations } from "./lib/google-maps.js"
import type { getAllUsers } from "./lib/users.js"

// @todo
// Add summaries for OpenAPI
// Add Authorization header only where needed
// Add correct response codes for errors
// Use Zod to form request and response bodies (zod-prisma)

const c = initContract()
export const contractRouter = c.router(
  {
    index: {
      method: "GET",
      path: "/",
      responses: {
        200: z.object({
          message: z.string(),
        }),
      },
    },
    getAuthData: {
      method: "GET",
      path: "/auth/data",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getSession>>>(),
      },
    },
    searchLocations: {
      method: "POST",
      path: "/maps/search",
      responses: {
        200: c.type<Awaited<ReturnType<typeof searchLocations>>>(),
      },
      body: c.type<Parameters<typeof searchLocations>[0]>(),
    },
    getIntegrations: {
      method: "GET",
      path: "/integrations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getIntegrations>>>(),
      },
    },
    getTimesForIntegrationByDay: {
      method: "GET",
      path: "/integrations/:integrationId/times",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getTimesForIntegrationByDay>>>(),
        404: c.type<{ message: string }>(),
      },
      query:
        c.type<
          Omit<
            Parameters<typeof getTimesForIntegrationByDay>[0],
            "integrationId"
          >
        >(),
    },
    getIcsForIntegration: {
      method: "GET",
      path: "/integrations/:integrationId/ics",
      responses: {
        200: c.otherResponse({
          contentType: "text/calendar",
          body: c.type<Awaited<ReturnType<typeof getIcsForIntegration>>>(),
        }),
      },
    },
    updateIntegration: {
      method: "POST",
      path: "/integrations/:integrationId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof updateIntegration>>>(),
      },
      body: c.type<
        Omit<
          Parameters<typeof updateIntegration>[0],
          "userId" | "integrationId"
        >
      >(),
    },
    deleteIntegration: {
      method: "DELETE",
      path: "/integrations/:integrationId",
      responses: {
        200: c.type<Awaited<ReturnType<typeof deleteIntegration>>>(),
      },
      body: z.any(),
    },
    sendNotificationsForIntegration: {
      method: "POST",
      path: "/integrations/:integrationId/send-notifications",
      responses: {
        200: c.noBody(),
      },
      body: c.noBody(),
    },
    getMosques: {
      method: "GET",
      path: "/mosques",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getMosques>>>(),
      },
    },
    importTimesForLondon: {
      method: "POST",
      path: "/times/import-london-unified",
      responses: {
        200: c.type<Awaited<ReturnType<typeof importTimesForLondon>>>(),
      },
      body: c.type<Parameters<typeof importTimesForLondon>[0]>(),
    },
    createIntegration: {
      method: "POST",
      path: "/integrations",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createIntegration>>>(),
      },
      body: c.noBody(),
    },
    enableSkill: {
      method: "POST",
      path: "/amazon/enable-skill",
      responses: {
        200: c.type<Awaited<ReturnType<typeof enableSkillForUser>>>(),
      },
      body: c.type<{ amazonAuthCode: string }>(),
    },
    getAmazonAuthorizeUrlForSkillLinking: {
      method: "GET",
      path: "/amazon/skill-linking-url",
      responses: {
        200: c.type<{ url: string }>(),
        404: c.type<{ message: string }>(),
      },
      query: c.type<{ integrationId: string }>(),
    },
    createCheckoutSession: {
      method: "POST",
      path: "/stripe/checkout-session",
      responses: {
        200: c.type<Awaited<ReturnType<typeof createCheckoutSession>>>(),
      },
      body: c.noBody(),
    },
    getStripePortalLink: {
      method: "GET",
      path: "/stripe/portal-url",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getStripePortalLink>>>(),
      },
    },
    scheduleSendNotifications: {
      method: "POST",
      path: "/scripts/schedule-send-notifications",
      responses: {
        200: c.type<Awaited<ReturnType<typeof scheduleSendNotifications>>>(),
      },
      body: c.noBody(),
    },
    sendNotificationsForMinute: {
      method: "GET",
      path: "/scripts/send-notifications-for-minute",
      responses: {
        200: c.type<Awaited<ReturnType<typeof sendNotificationsForMinute>>>(),
      },
      query: c.type<Parameters<typeof sendNotificationsForMinute>[0]>(),
    },
    storeNotificationsForHour: {
      method: "GET",
      path: "/scripts/store-notifications-for-hour",
      responses: {
        200: c.type<Awaited<ReturnType<typeof storeNotificationsForHour>>>(),
      },
      query: c.type<Parameters<typeof storeNotificationsForHour>[0]>(),
    },
    getAllUsers: {
      method: "GET",
      path: "/users",
      responses: {
        200: c.type<Awaited<ReturnType<typeof getAllUsers>>>(),
      },
    },
  },
  {
    baseHeaders: z.object({
      authorization: z.optional(z.string()),
      "emulate-user-id": z.string().optional(),
    }),
  }
)
